/**
 * Base-indicator choropleth — spec §F5.3 — **and its §F5.4 bivariate mode**.
 *
 * One component owns the fill layer in both modes, deliberately. §F5.4's "Exit via seg or
 * legend ✕ → restore single rendering" is a *swap*, and two sibling components each adding
 * their own `L.geoJSON` would mean a frame with both on the map (or, worse, an ordering
 * where the single fill is removed after the bivariate one is added and the map blinks
 * empty). Here the mode is one branch inside the single effect that builds the layer, so
 * React tears the old layer down and builds the new one in the same commit — the mockup's
 * `renderChoropleth()`, which also had exactly one `choroLayer`.
 *
 * "Data: units GeoJSON (level+country) + values (D3) joined on unit_id. Classify per
 * `indStyle` (G1–G3), paint fillColor per class, white 1.1 stroke, fillOpacity =
 * indStyle.opacity (default .78). Hover: weight 2.6 dark stroke + tooltip … Click → Unit
 * profile (F5.7). Count indicators: `toLocaleString` no decimals. Re-render on: indicator,
 * year, level, country, style changes."
 *
 * The queries and the classification live in `useChoroplethClasses()` (see that file for
 * why they are a hook rather than a store slice) — the §F5.6 legend calls the same hook, so
 * the swatch ranges are by construction the ranges this layer painted. Both queries are
 * gated on `indicator !== null`; when it clears, the hook returns null, the effect's
 * cleanup removes the Leaflet layer, and that is the whole of §F5.2's "hide the
 * choropleth".
 */
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import L from 'leaflet';

import { type UnitProperties } from '../../lib/api';
import { fmtNum } from '../../lib/format';
import { escapeHtml } from '../../lib/html';
import { levelLabelKey, useAppStore } from '../../lib/state';
import { bivColor, bivLabel, classOf } from '../../lib/symbology';
import { useBivariateClasses, type BivVariable } from './bivClasses';
import { useChoroplethClasses } from './choroClasses';
import { useMap } from './mapContext';

type GeoJsonInput = Parameters<typeof L.geoJSON>[0];

/**
 * §F5.3 does not say what to paint for a unit the API omitted (§D3 omits units with no
 * data). It must not be class 0: that reads as "very low" when the truth is "unknown".
 * `--line-2` at half opacity is the mockup's inert panel grey — visibly not part of the
 * ramp, and still lets the basemap through. Logged as NOTES #187.
 */
const NO_DATA_FILL = '#EEF1EC';
const NO_DATA_OPACITY = 0.5;

/** §F5.4: "fill `BIV[yc][xc]` (Part A matrix), opacity .84". The stroke stays §F5.3's. */
const BIV_FILL_OPACITY = 0.84;
/** The mockup's colour for the `low / high` line in the bivariate tooltip — `BIV[2][2]`. */
const BIV_TIP_INK = '#2A5A5B';

/**
 * What the paint and tooltip closures read while no indicator is mapped.
 *
 * They are built on every render — they have to be, so the refs Leaflet reads through are
 * current — but `useChoroplethClasses()` answers `null` when §F5.2 has cleared the
 * indicator. Nothing is drawn in that state (the build effect early-returns and its cleanup
 * has already removed the layer), so this is a shape, not a fallback anyone sees.
 */
const EMPTY_CLASSES = {
  breaks: [] as number[],
  colors: [] as string[],
  values: new Map<string, number>(),
  fmt: 1,
  unitLabel: '',
  level: 1 as const,
  year: 0,
  config: undefined,
};

export function Choropleth() {
  const { t } = useTranslation();
  const map = useMap();
  const indStyle = useAppStore((s) => s.indStyle);
  const setSelectedUnit = useAppStore((s) => s.setSelectedUnit);
  const cls = useChoroplethClasses();
  // Null unless §F5.4 is armed (bivariate on **and** a Y picked) — so `biv.on` with no Y
  // falls straight through to the single-indicator paint below, which is what §F5.4's
  // "keep base as X" looks like while the reader is still choosing.
  const biv = useBivariateClasses();

  const units = cls?.units;
  const ready = Boolean(cls?.ready);

  /**
   * §F5.3's paint and tooltip, as of *this render*, reachable from a Leaflet callback.
   *
   * The layer used to be torn down and rebuilt on any `indStyle` change — 1048 `L.geoJSON`
   * features, 1048 tooltips and 1048 event bindings **per frame** of the opacity slider,
   * and again on every year tick of the §F5.10 play loop. Nothing about the geometry
   * changes in either case; only the fill does.
   *
   * So the two things that vary go through refs, and Leaflet reads them lazily: `style`
   * is a function, and `bindTooltip` accepts a function evaluated when the tooltip opens.
   * The layer is then built exactly once per *geometry* (a level or country change), and a
   * restyle is `setStyle()` — a path attribute update, no DOM churn, no re-binding.
   */
  const styleRef = useRef<(props: UnitProperties) => L.PathOptions>(() => ({}));
  const tooltipRef = useRef<(props: UnitProperties) => string>(() => '');
  const layerRef = useRef<L.GeoJSON | null>(null);

  {
    const { breaks, colors, values, fmt, unitLabel, level, year, config } = cls ?? EMPTY_CLASSES;
    const countries = config?.countries ?? {};
    // The bivariate paint only takes over once its own payload has landed; until then the
    // single fill stays on screen rather than blinking to no-data (§F5.11 "no flicker").
    const bi = biv && biv.ready ? biv : null;

    const singleStyle = (props: UnitProperties): L.PathOptions => {
      const v = values.get(props.id);
      if (typeof v !== 'number' || !Number.isFinite(v)) {
        return { color: '#fff', weight: 1.1, fillColor: NO_DATA_FILL, fillOpacity: NO_DATA_OPACITY };
      }
      return {
        color: '#fff',
        weight: 1.1,
        fillColor: colors[classOf(v, breaks)],
        fillOpacity: indStyle.opacity,
      };
    };

    /**
     * §F5.4/§G5: independent tertile classes per variable, crossed through the Part A
     * matrix. A unit missing **either** value gets the §F5.3 no-data paint — a bivariate
     * cell is a statement about two numbers, and inventing one of them is worse than an
     * honest grey.
     */
    const bivStyle = (props: UnitProperties): L.PathOptions => {
      const xv = bi!.x.values.get(props.id);
      const yv = bi!.y.values.get(props.id);
      if (typeof xv !== 'number' || typeof yv !== 'number' || !Number.isFinite(xv) || !Number.isFinite(yv)) {
        return { color: '#fff', weight: 1.1, fillColor: NO_DATA_FILL, fillOpacity: NO_DATA_OPACITY };
      }
      return {
        color: '#fff',
        weight: 1.1,
        fillColor: bivColor(classOf(xv, bi!.x.breaks), classOf(yv, bi!.y.breaks), bi!.matrix),
        fillOpacity: BIV_FILL_OPACITY,
      };
    };

    /** §F5.4: "Tooltip `**unit** — {xLabel} / {yLabel}` + both values." */
    const bivTooltip = (props: UnitProperties): string => {
      const xv = bi!.x.values.get(props.id);
      const yv = bi!.y.values.get(props.id);
      const known = typeof xv === 'number' && typeof yv === 'number';
      const cross = known
        ? `<b style="color:${BIV_TIP_INK}">${bivLabel(classOf(xv, bi!.x.breaks))} / ${bivLabel(
            classOf(yv, bi!.y.breaks),
          )}</b>`
        : t('choropleth.noData');
      // `axis.name`/`axis.unitLabel` are `/indicators` text and `props.name` is a unit name
      // from `/hierarchy/units`: server strings reaching `innerHTML` (see `lib/html.ts`).
      const line = (v: number | undefined, axis: BivVariable) =>
        `${escapeHtml(axis.name)}: ${
          typeof v === 'number'
            ? `${fmtNum(v, axis.fmt)} ${escapeHtml(axis.unitLabel)}`
            : t('choropleth.noData')
        }`;
      return `<b>${escapeHtml(props.name)}</b> — ${cross}<br>${line(xv, bi!.x)}<br>${line(yv, bi!.y)}`;
    };

    /** §F5.3: "**unit** — value unit" over the scope line. */
    const singleTooltip = (props: UnitProperties): string => {
      const v = values.get(props.id);
      // A watershed feature has no `admin_level`; the reporting level then *is* the level,
      // and `levelLabelKey` names it (§M3.5).
      const lvl = props.admin_level ?? level;
      const scope = [
        countries[props.country ?? ''] ?? props.country ?? '',
        lvl === 0 ? '' : t(levelLabelKey(lvl)),
        String(year),
      ]
        .filter(Boolean)
        .join(' · ');
      const value =
        typeof v === 'number' && Number.isFinite(v)
          ? ` — ${fmtNum(v, fmt)} ${escapeHtml(unitLabel)}`
          : ` — ${t('choropleth.noData')}`;
      return (
        `<b>${escapeHtml(props.name)}</b>${value}` +
        `<br><span style="color:#8B958A;font-weight:400">${escapeHtml(scope)}</span>`
      );
    };

    styleRef.current = (props) => (bi ? bivStyle(props) : singleStyle(props));
    tooltipRef.current = (props) => (bi ? bivTooltip(props) : singleTooltip(props));
  }

  /**
   * Build the layer — **once per geometry**. `units` is a new object only when the level,
   * the country or the reporting hierarchy changes; everything else the layer draws is read
   * through the refs above, at the moment Leaflet needs it.
   *
   * `ready` and not `hasData`: a year with **no** values anywhere must still draw the grey
   * no-data layer, so the map says "we asked and there is nothing" instead of silently
   * reverting to the previous year's paint (Task 5 review carry-over).
   */
  useEffect(() => {
    if (!ready || !units) return;
    const layer = L.geoJSON(units as unknown as GeoJsonInput, {
      pane: 'choroPane',
      style: (feature) => styleRef.current((feature as { properties: UnitProperties }).properties),
      onEachFeature: (feature, ly) => {
        const props = feature.properties as UnitProperties;
        // A *function* tooltip: Leaflet calls it when the tooltip opens, so the content is
        // always the current year's number and the current mode's shape without the layer
        // having to be rebuilt to carry them.
        ly.bindTooltip(() => tooltipRef.current(props), { sticky: true, className: 'unit-tip' });
        ly.on('click', () => setSelectedUnit(props.id));
        ly.on('mouseover', () => {
          if ('setStyle' in ly) (ly as L.Path).setStyle({ weight: 2.6, color: '#22301F' });
        });
        // `resetStyle` re-applies `options.style`, which is the ref-reading function above —
        // so a hover that ends after a restyle returns the polygon to its *new* fill.
        ly.on('mouseout', () => {
          if ('setStyle' in ly) layer.resetStyle(ly as L.Path);
        });
      },
    });
    layer.addTo(map);
    layerRef.current = layer;
    return () => {
      map.removeLayer(layer);
      layerRef.current = null;
    };
  }, [map, units, ready, setSelectedUnit]);

  /**
   * Repaint — style only. Every §F5.2/§F5.6 restyle (opacity drag, class count, method,
   * ramp, bivariate on/off) and every year tick lands here instead of in a rebuild.
   */
  useEffect(() => {
    layerRef.current?.setStyle((feature) =>
      styleRef.current((feature as { properties: UnitProperties }).properties),
    );
  }, [cls, biv, indStyle]);

  return null;
}

export default Choropleth;
